import { getRpcBaseUrl } from '@src/config';
import { sleep } from '@src/utils/utils';
import { JsonRpc } from 'eosjs/dist/eosjs-jsonrpc';
import ecc from 'eosjs-ecc';

export interface GetTableRowRequest {
    contract: string;
    scope: string;
    table: string;
    key: string;
    keyField: string;
}

const rpc = new JsonRpc(getRpcBaseUrl(), { fetch });

export const getRpc = () => rpc;

export const getTableRow = async (payload: GetTableRowRequest) => {
    const { contract, scope, table, key, keyField } = payload;

    return retry(async () => {
        const result = await rpc.get_table_rows({
            code: contract,
            scope,
            table,
            lower_bound: key,
            limit: 1,
            reverse: false,
            show_payer: false,
            json: true,
        });
        const firstRow = result.rows[0];
        // The first returned row might not be the one searched for,
        // so we verify using the keyField.
        if (!firstRow || firstRow[keyField]?.toString() !== key) return { row: null };

        return { row: firstRow };
    });
};

export const getTableRows = async (code: string, scope: string, table: string, lowerBound: string | null, limit = 10, reverse = false) => {
    return retry(async () => {
        return rpc.get_table_rows({
            code,
            scope,
            table,
            limit,
            lower_bound: lowerBound,
            upper_bound: null,
            reverse,
            show_payer: false,
            json: true,
        });
    });
};

export const sign = (payload, privateKey: string): string => {
    return ecc.sign(payload, privateKey);
};

export interface RetryOptions {
    times?: number;
    awaitTime?: number;
}

export async function retry<T extends () => Promise<unknown>>(fn: T, options?: RetryOptions) {
    const retries = options?.times || 6;
    const awaitTime = options?.awaitTime || 500;

    for (let i = 1; i <= retries; i++) {
        const result = await fn().catch(async () => {});
        if (result) return result;
        await sleep(2 ** i * awaitTime);
    }
}
